// ─────────────────────────────────────────────
// Oike — Finanzas › Movimientos › Previsión (pure; tested by scripts/test-forecast.mjs). Approved by Ale on 04-11-2026.
// A forecast of MOVEMENTS, never of money available: there are no accounts or opening balances. Computed when opened,
// never stored; it creates no tasks, expenses or movements and changes no dates. No opportunities.
// - Cobros previstos: open installments with their CURRENT balance (chargeState: partial payments count, a rest moved to
//   a new installment lives only there — the original amount is never counted again), by due date.
// - Pagos previstos: expenses with their current pending balance (expenseState), by due date. Business and personal
//   are separate views; personal never adds to business.
// - Overdue (due before today) and pending without a date: their own blocks, never inside the periods.
// - Suggested subscriptions: the next valid periods of a repeating series inside the horizon (series + period; periods
//   cancelled explicitly never come back), estimated from the last known amount; apart from confirmed commitments, never
//   an obligation, never created. Gaps between handled periods and past periods not registered: review, informative.
// - Doubtful amounts, incomplete operations and «requiere revisión»: listed apart, never zero nor definitive; when they
//   touch a currency the forecast of that currency is marked incomplete.
// - Alerts: negative net movements per week and per month (confirmed only), grouped when they describe the same records,
//   always saying they do not prove a lack of money.
// Words never used: beneficio, rentabilidad, EBITDA, dinero disponible.
// ─────────────────────────────────────────────
import { realRecords } from './finance.js';
import { chargeState } from './charges.js';
import { proposalOfVersion } from './payments.js';
import { chainsOf } from './quoteChains.js';
import { isAccepted } from './quoteRules.js';
import { isTestName } from './testRecords.js';
import { CHARGE, PAYMENT, APPLICATION } from './billingSchema.js';
import { seriesOf } from './expenses.js';

const cents = (n) => Math.round(Number(n || 0) * 100);
const money = (c) => Math.round(c) / 100;
const links = (v) => (Array.isArray(v) ? v.filter(Boolean) : []);
const sel = (v) => (v && typeof v === 'object' ? v.name : v) || '';
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const addDays = (day, n) => { const d = new Date(`${day}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
const lastDayOf = (ym) => { const [y, m] = ym.split('-').map(Number); return `${ym}-${String(new Date(Date.UTC(y, m, 0)).getUTCDate()).padStart(2, '0')}`; };
const nextMonth = (ym) => { const [y, m] = ym.split('-').map(Number); return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`; };
export const CURRENCY_ORDER = ['EUR', 'USD', 'BOB'];
const curOrder = (a, b) => (CURRENCY_ORDER.indexOf(a) + 1 || 99) - (CURRENCY_ORDER.indexOf(b) + 1 || 99) || a.localeCompare(b);

export const SCOPE_TEXT = {
  business: 'Negocio. Cobros previstos: lo que falta cobrar de cada cuota abierta, por su vencimiento. Pagos previstos: lo que falta pagar de los gastos del negocio, por su vencimiento. Sin oportunidades comerciales, sin saldos de cuentas: es una previsión de movimientos, no del dinero que tendrás.',
  personal: 'Personal. Pagos previstos: lo que falta pagar de tus gastos personales, por su vencimiento. Nunca se suma al negocio. Sin saldos de cuentas: es una previsión de movimientos, no del dinero que tendrás.',
};
export const ALERT_NOTE = 'Un neto negativo significa que en ese período vencen más pagos que cobros registrados. No demuestra falta de dinero: Oike no conoce tus saldos iniciales.';
export const SUGGESTION_NOTE = 'Importe estimado desde el último registrado de la serie. Una sugerencia no demuestra una obligación confirmada.';
export const REVIEW_TEXT = {
  unconfirmed_payment: 'Hay un pago recibido sin confirmar aplicado a esta cuota: el saldo puede ser menor.',
  integrity_issue: 'Un pago aplicado a esta cuota está marcado con una inconsistencia.',
  currency_missing: 'Sin moneda registrada.', amount_missing: 'Sin importe válido.',
  earlier_unresolved: 'Hay cuotas de una versión anterior por resolver: no hay saldo exigible definitivo.',
  paid_above_agreed: 'Lo cobrado supera lo acordado en esta versión: por revisar.',
  legacy_payment: 'Estado de pago anterior a los cobros, sin importe: nunca se convierte en dinero.',
  overpaid: 'Pagos por encima del importe: requiere revisión.', incomplete_operation: 'Un pago quedó a medio registrar.',
  payment_multiple_links: 'Un pago está vinculado a varios gastos.', payment_other_currency: 'Un pago en otra moneda: el saldo queda abierto.',
  same_key_conflict: 'Dos registros comparten la misma operación con contenidos distintos.',
  period_gap: 'Período sin registrar entre meses registrados de esta serie. Revisalo: no se asume una deuda ni se suma a la previsión.',
  period_not_registered: 'Período ya pasado sin registrar. Revisalo: no se registra ni se suma a la previsión.',
};

/** The periods: today's rolling weeks over 30 days, and the rest of this month plus the next two. */
export function periodsOf(today) {
  const end30 = addDays(today, 29);
  const weeks = [];
  for (let s = today; s <= end30; s = addDays(s, 7)) { const e = addDays(s, 6); weeks.push({ key: `w:${s}`, from: s, to: e < end30 ? e : end30 }); }
  const months = []; let ym = today.slice(0, 7);
  for (let i = 0; i < 3; i++) { months.push({ key: `m:${ym}`, month: ym, from: i === 0 ? today : `${ym}-01`, to: lastDayOf(ym) }); ym = nextMonth(ym); }
  return { today, end30, end3m: months[2].to, weeks, months };
}

// ── Sources → movements ──
/** Income (business): open installments with their current balance, accepted contracts not organized in installments, and what to review. */
export function incomeMovements(raw = {}, intakes = [], names = {}, today) {
  const out = { dated: [], undated: [], review: [] };
  const allPayments = raw.payments || [];
  const real = realRecords(raw);
  for (const c of real.charges) {
    const f = c.fields || {};
    if (sel(f[CHARGE.status]) === 'cancelled') continue;
    const st = chargeState(c, { applications: real.applications, payments: real.payments, today });
    if (!(st.pending > 0)) continue;
    const payerId = links(f[CHARGE.stakeholder])[0] || links(f[CHARGE.account])[0];
    const item = {
      kind: 'charge', direction: 'in', id: c.id, date: String(f[CHARGE.dueOn] || ''), amount: st.pending, currency: f[CHARGE.currency] || '',
      label: `${f[CHARGE.concept] || f[CHARGE.name] || 'Cuota'}${names[payerId] ? ` · ${names[payerId]}` : ''}`, collected: st.collected, original: st.amount,
      open: { page: 'quotes', chargeId: c.id, quoteId: links(f[CHARGE.quote])[0] || '' },
    };
    const reasons = [];
    const mineApps = (raw.applications || []).filter(a => links(a.fields?.[APPLICATION.charge]).includes(c.id) && sel(a.fields?.[APPLICATION.status]) === 'active');
    for (const a of mineApps) {
      const p = allPayments.find(x => links(a.fields?.[APPLICATION.payment]).includes(x.id));
      if (!p) continue;
      if (sel(p.fields?.[PAYMENT.status]) === 'pending') reasons.push('unconfirmed_payment');
      if (p.fields?.[PAYMENT.integrityIssue]) reasons.push('integrity_issue');
    }
    if (!item.currency) reasons.push('currency_missing');
    if (!(Number(f[CHARGE.amount]) > 0)) reasons.push('amount_missing');
    if (reasons.length) out.review.push({ ...item, reasons: [...new Set(reasons)] });
    else if (DAY_RE.test(item.date)) out.dated.push(item);
    else out.undated.push({ ...item, date: '' });
  }
  // Accepted contracts with an amount not organized in installments yet (agreed − collected before − planned), and what blocks a payable balance
  const ctx = { intakes, ...raw };
  for (const chain of chainsOf(intakes)) {
    const sale = chain.sale;
    if (!sale || !isAccepted(sale.record.fields || {})) continue;
    const f = sale.record.fields || {};
    if (isTestName(String(f['Name'] || ''))) continue;
    const prop = proposalOfVersion(sale.id, ctx);
    if (!prop) continue;
    const base = { kind: 'quote', direction: 'in', id: sale.id, currency: prop.currency, label: f['Name'] || 'Contratación', open: { page: 'quotes', quoteId: sale.id } };
    const reasons = [];
    if (prop.unresolvedEarlier?.length) reasons.push('earlier_unresolved');
    if (prop.paidAboveAgreed > 0) reasons.push('paid_above_agreed');
    if (prop.legacyPayment) reasons.push('legacy_payment');
    if (reasons.length) { out.review.push({ ...base, amount: prop.notPlanned || null, date: '', reasons }); continue; }
    if (prop.notPlanned > 0) out.undated.push({ ...base, amount: prop.notPlanned, date: '', note: 'Aceptada, sin cuotas organizadas por este importe' });
    if (prop.monthly > 0 && !prop.hasPlan) out.undated.push({ ...base, id: `${sale.id}:monthly`, amount: null, monthly: prop.monthly, date: '', note: `Compromiso mensual de ${prop.monthly}, sin cuotas ni duración` });
  }
  return out;
}

/** Expenses of one scope (the views of Gastos, with their current state). */
export function expenseMovements(list = [], scope) {
  const out = { dated: [], undated: [], review: [] };
  for (const x of list) {
    const s = x.state || {};
    if (s.state === 'cancelled' || s.state === 'paid') continue;
    if (isTestName(String(x.concept || ''))) continue;
    const item = { kind: 'expense', direction: 'out', scope, id: x.id, date: x.dueOn || '', amount: s.pending, currency: x.currency || '', label: `${x.concept || 'Gasto'}${x.supplier ? ` · ${x.supplier}` : ''}`, paid: s.paid, original: x.amount, open: { page: 'expenses', scope, expenseId: x.id } };
    const reasons = [...(s.review || [])];
    if (!item.currency) reasons.push('currency_missing');
    if (!(Number(x.amount) > 0)) reasons.push('amount_missing');
    if (reasons.length || s.pending === null) { out.review.push({ ...item, amount: s.pending, reasons: [...new Set(reasons.length ? reasons : ['overpaid'])] }); continue; }
    if (!(s.pending > 0)) continue;
    if (DAY_RE.test(item.date)) out.dated.push(item); else out.undated.push({ ...item, date: '' });
  }
  return out;
}

/** The estimated day of a period: the same day (and month, if yearly) as the series' latest record, clipped to the month. */
function estimatedDay(period, rec, ref) {
  const day = Number((ref || '').slice(8, 10)) || 1;
  const ym = rec === 'yearly' ? `${period}-${(ref || '').slice(5, 7) || '01'}` : period;
  const last = Number(lastDayOf(ym).slice(8, 10));
  return `${ym}-${String(Math.min(day, last)).padStart(2, '0')}`;
}
const nextP = (p, rec) => (rec === 'yearly' ? String(Number(p) + 1) : nextMonth(p));
const viewItem = (x) => ({ id: x.id, cancelled: x.status === 'cancelled' || x.state?.state === 'cancelled', period: x.period || '', incurredOn: x.incurredOn || '', dueOn: x.dueOn || '',
  recurrence: x.recurrence || 'none', seriesKey: x.seriesKey || '', amount: Number(x.amount) || 0, currency: x.currency || '', concept: x.concept || '', supplier: x.supplier || '' });
/**
 * Subscription periods (the rule of Gastos, src/expenses.js › seriesOf): the next valid periods up to `until`, from today,
 * as suggestions (never an obligation, never created). Periods cancelled explicitly are never suggested again. Gaps
 * between handled periods and past periods not registered go to review: informative, no amount, never a debt.
 */
export function subscriptionPeriods(list = [], scope, { until, today, max = 40 } = {}) {
  const suggested = []; const review = [];
  for (const s of seriesOf(list.map(viewItem))) {
    const l = s.latest;
    const base = { direction: 'out', scope, seriesKey: s.key, recurrence: s.recurrence, currency: l.currency, label: `${l.concept || 'Suscripción'}${l.supplier ? ` · ${l.supplier}` : ''}`, fromId: l.id, open: { page: 'expenses', scope, expenseId: l.id } };
    for (const g of s.gaps) review.push({ ...base, kind: 'period', id: `${s.key}:${g}:gap`, period: g, date: '', amount: null, informative: true, reasons: ['period_gap'] });
    if (!s.repeats) continue;
    const ref = l.dueOn || l.incurredOn;
    let p = s.lastHandled;
    for (let i = 0; i < max; i++) {
      p = nextP(p, s.recurrence);
      const date = estimatedDay(p, s.recurrence, ref);
      if (date > until) break;
      if (s.registered.has(p) || s.cancelled.has(p)) continue;
      if (date < today) { review.push({ ...base, kind: 'period', id: `${s.key}:${p}:past`, period: p, date: '', amount: null, informative: true, reasons: ['period_not_registered'] }); continue; }
      suggested.push({ ...base, kind: 'suggested', id: `${s.key}:${p}`, period: p, date, amount: l.amount });
    }
  }
  return { suggested: suggested.sort((a, b) => a.date.localeCompare(b.date)), review };
}

// ── Aggregation ──
const blank = () => ({ inC: 0, outC: 0, suggestedC: 0, ids: [], inIds: [], outIds: [] });
const finish = (b, { incomeKnown, expensesKnown }) => ({
  in: incomeKnown ? money(b.inC) : null, out: expensesKnown ? money(b.outC) : null,
  diff: incomeKnown && expensesKnown ? money(b.inC - b.outC) : null, suggested: money(b.suggestedC), withSuggested: incomeKnown && expensesKnown ? money(b.inC - b.outC - b.suggestedC) : null,
  ids: b.ids,
});

/** One view (business or personal), per currency: summaries, weeks, months, list, blocks and alerts. */
export function viewOf({ income = null, expenses = null, suggested = [], periodReview = [], periods, incomeApplies = true }) {
  const { today, end30, end3m, weeks, months } = periods;
  const incomeKnown = !incomeApplies || !!income; const expensesKnown = !!expenses;
  const dated = [...(incomeApplies && income ? income.dated : []), ...(expenses ? expenses.dated : [])];
  const currencies = new Set([...dated, ...suggested, ...periodReview, ...(income?.undated || []), ...(income?.review || []), ...(expenses?.undated || []), ...(expenses?.review || [])].map(m => m.currency).filter(Boolean));
  const byCurrency = {};
  const noCurrencyCount = [...(incomeApplies && income ? income.review : []), ...(expenses ? expenses.review : [])].filter(m => !m.currency).length;
  for (const cur of [...currencies].sort(curOrder)) {
    const mine = dated.filter(m => m.currency === cur);
    const sug = suggested.filter(m => m.currency === cur);
    const add = (b, m) => { if (m.kind === 'suggested') { b.suggestedC += cents(m.amount); return; } if (m.direction === 'in') { b.inC += cents(m.amount); b.inIds.push(m.id); } else { b.outC += cents(m.amount); b.outIds.push(m.id); } b.ids.push(m.id); };
    const range = (from, to) => { const b = blank(); for (const m of [...mine, ...sug]) if (m.date >= from && m.date <= to) add(b, m); return b; };
    const ctx = { incomeKnown: incomeKnown || !incomeApplies, expensesKnown };
    const review = [...(incomeApplies && income ? income.review : []), ...(expenses ? expenses.review : []), ...periodReview].filter(m => m.currency === cur);
    const weekRows = weeks.map(w => ({ key: w.key, from: w.from, to: w.to, ...finish(range(w.from, w.to), ctx) }));
    const monthRows = months.map(m => ({ key: m.key, month: m.month, from: m.from, to: m.to, ...finish(range(m.from, m.to), ctx) }));
    byCurrency[cur] = {
      summary: { d30: finish(range(today, end30), ctx), m3: finish(range(today, end3m), ctx) },
      weeks: weekRows, months: monthRows,
      list: [...mine, ...sug].filter(m => m.date >= today && m.date <= end3m).sort((a, b) => a.date.localeCompare(b.date) || (a.direction === 'in' ? -1 : 1)),
      overdue: mine.filter(m => m.date < today).sort((a, b) => a.date.localeCompare(b.date)),
      later: mine.filter(m => m.date > end3m).length,
      undated: [...(incomeApplies && income ? income.undated : []), ...(expenses ? expenses.undated : [])].filter(m => m.currency === cur),
      review,
      // Periods to review are informative: they do not change the calculation, so they do not make it incomplete
      incomplete: review.some(m => !m.informative) || noCurrencyCount > 0 || !expensesKnown || (incomeApplies && !income),
      alerts: alertsOf(cur, weekRows, monthRows),
    };
  }
  const noCurrency = [...(incomeApplies && income ? income.review : []), ...(expenses ? expenses.review : [])].filter(m => !m.currency);
  return { byCurrency, noCurrency, errors: { income: incomeApplies && !income, expenses: !expensesKnown } };
}

/** Negative net (confirmed movements) per week and per month; a month and a week with the same records are one alert. */
export function alertsOf(currency, weeks = [], months = []) {
  const neg = (r) => r.diff !== null && r.diff < 0;
  const key = (ids) => [...ids].sort().join('|');
  const alerts = [];
  for (const w of weeks.filter(neg)) alerts.push({ currency, periods: [{ kind: 'week', from: w.from, to: w.to }], diff: w.diff, ids: w.ids, key: key(w.ids) });
  for (const m of months.filter(neg)) {
    const same = alerts.find(a => a.key === key(m.ids));
    if (same) { same.periods.push({ kind: 'month', month: m.month, from: m.from, to: m.to }); continue; }
    alerts.push({ currency, periods: [{ kind: 'month', month: m.month, from: m.from, to: m.to }], diff: m.diff, ids: m.ids, key: key(m.ids) });
  }
  return alerts.map(({ key: _k, ...a }) => ({ ...a, note: ALERT_NOTE }));
}

/** The whole forecast. income: { raw, intakes, names } | null; business/personal: views of Gastos | null (could not be read). */
export function forecastOf({ income = null, business = null, personal = null, today = new Date().toISOString().slice(0, 10) } = {}) {
  const periods = periodsOf(today);
  const inc = income ? incomeMovements(income.raw, income.intakes, income.names, today) : null;
  const biz = business ? expenseMovements(business, 'business') : null;
  const per = personal ? expenseMovements(personal, 'personal') : null;
  const bizSubs = business ? subscriptionPeriods(business, 'business', { until: periods.end3m, today }) : { suggested: [], review: [] };
  const perSubs = personal ? subscriptionPeriods(personal, 'personal', { until: periods.end3m, today }) : { suggested: [], review: [] };
  return {
    today, periods,
    business: { scope: SCOPE_TEXT.business, ...viewOf({ income: inc, expenses: biz, suggested: bizSubs.suggested, periodReview: bizSubs.review, periods }) },
    personal: { scope: SCOPE_TEXT.personal, ...viewOf({ expenses: per, suggested: perSubs.suggested, periodReview: perSubs.review, periods, incomeApplies: false }) },
    notes: { alert: ALERT_NOTE, suggestion: SUGGESTION_NOTE },
  };
}
